import { Layout } from '../../shared/components/layout/Layout';
import {
  AtSymbolIcon,
  BuildingOffice2Icon,
  ChevronRightIcon,
  LockClosedIcon,
  TruckIcon,
  UserGroupIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { css } from '@emotion/css';
import { Colors } from '../../shared/styles/colors';
import {
  h2Style,
  h3Style,
  p2Style,
  valueStyle,
} from '../../shared/styles/text';
import { useNavigator } from '../../routing/navigator';
import {
  Button,
  ButtonSizeEnum,
  ButtonVariantEnum,
} from '../../shared/components/Button';
import { authenticationStore } from '../../shared/stores/authentification-store';
import { useCurrentUser } from '../../shared/services/api/user';
import { useQueryClient } from '@tanstack/react-query';
import { CacheKeys } from '../../shared/services/api/cache-configuration';
import { useUser } from '../../shared/providers/user-provider';
import { UserRole } from '../../shared/types/enums';
import { BOTTOM_NAV_SAFE_HEIGHT } from '../../shared/styles/css-values';
import { Motion, Radius, unstyledButton } from '../../shared/styles/tokens';

export const AccountPage = (): JSX.Element => {
  const user = useUser()!;
  const navigator = useNavigator();
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();

  const handleUserLogout = async () => {
    window.localStorage.removeItem('token');
    authenticationStore.reset();
    await queryClient.invalidateQueries({ queryKey: [CacheKeys.ME] });
    await currentUser.refetch();
    navigator.toLanding();
  };

  const initials = (user?.name ?? '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

  const options: {
    label: string;
    icon: JSX.Element;
    onClick: () => void;
    show: boolean;
  }[] = [
    {
      label: 'Personnel',
      icon: <UserGroupIcon className={iconStyle} />,
      onClick: navigator.toPersonnelPage,
      show: user.role === UserRole.CHARTER_OWNER,
    },
    {
      label: 'Personal Information',
      icon: <UserIcon className={iconStyle} />,
      onClick: navigator.toPersonalInformation,
      show: true,
    },
    {
      label: 'Change Email',
      icon: <AtSymbolIcon className={iconStyle} />,
      onClick: navigator.toChangeEmail,
      show: !!user?.email,
    },
    {
      label: 'Change Username',
      icon: <AtSymbolIcon className={iconStyle} />,
      onClick: navigator.toChangeUsername,
      show: !!user?.username,
    },
    {
      label: 'Change Password',
      icon: <LockClosedIcon className={iconStyle} />,
      onClick: navigator.toPasswordPage,
      show: true,
    },
    {
      label: 'Change Company Invoice Information',
      icon: <BuildingOffice2Icon className={iconStyle} />,
      onClick: navigator.toCompanyInvoiceInformation,
      show: true,
    },
    {
      label: 'Vehicles',
      icon: <TruckIcon className={iconStyle} />,
      onClick: navigator.toVehicles,
      show: true,
    },
  ];

  return (
    <Layout.Content className={accountPageStyle}>
      <div className={accountPageHeaderContainerStyle}>
        <div className={identityStyle}>
          <div className={avatarStyle} aria-hidden="true">
            {initials || <UserIcon width={28} />}
          </div>
          <h1 className={accountPageHeaderStyle}>{user?.name}</h1>
        </div>
        <Button
          size={ButtonSizeEnum.MEDIUM}
          variant={ButtonVariantEnum.TEXT}
          text={'logout'}
          onClick={handleUserLogout}
        />
      </div>
      <div className={contactStyle}>
        {user?.email && <div className={accountEmailStyle}>{user?.email}</div>}
        {user?.username && (
          <div className={accountEmailStyle}>{user?.username}</div>
        )}
      </div>
      {user.charter && user.role !== UserRole.CHARTER_OWNER && (
        <div className={charterInfoStyle}>
          <div className={charterLabelStyle}>Charter info</div>
          <div>{user?.charter?.owner.name}</div>
          <div>{user?.charter?.owner.email}</div>
        </div>
      )}
      <ul className={accountOptionsListStyle}>
        {options
          .filter((option) => option.show)
          .map((option) => (
            <li key={option.label}>
              <button
                type="button"
                className={optionButtonStyle}
                onClick={option.onClick}
              >
                {option.icon}
                <span>{option.label}</span>
                <ChevronRightIcon className={chevronRightStyle} />
              </button>
            </li>
          ))}
      </ul>

      <span className={h3Style}>Legal</span>

      <ul className={legalOptionsListStyle}>
        <li>
          <button
            type="button"
            className={legalButtonStyle}
            onClick={navigator.toPrivacyPolicy}
          >
            Privacy Policy
          </button>
        </li>
        <li>
          <button
            type="button"
            className={legalButtonStyle}
            onClick={navigator.toTermsOfUse}
          >
            Terms of Use
          </button>
        </li>
        <li>
          <button
            type="button"
            className={legalButtonStyle}
            onClick={navigator.toTermsOfSale}
          >
            Terms of Sale
          </button>
        </li>
      </ul>
    </Layout.Content>
  );
};

const accountPageStyle = css`
  padding-top: 0.625rem;
  padding-bottom: calc(${BOTTOM_NAV_SAFE_HEIGHT} + 2rem);
`;

const accountPageHeaderContainerStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

const identityStyle = css`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  min-width: 0;
`;

const contactStyle = css`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  margin-top: 0.75rem;
`;

const avatarStyle = css`
  flex: 0 0 auto;
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 50%;
  background: ${Colors.CAPRI_BLUE_TINT};
  color: ${Colors.CAPRI_BLUE};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.125rem;
  letter-spacing: 0.02em;
`;

const accountPageHeaderStyle = css`
  ${h2Style}
  line-height: 130%;
  overflow-wrap: anywhere;
`;
const charterInfoStyle = css`
  ${p2Style}
  color: ${Colors.TEXT_SECONDARY};
  line-height: 130%;
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.1875rem;
  padding: 0.75rem 1rem;
  background: ${Colors.SURFACE};
  border-radius: ${Radius.md};
`;

const charterLabelStyle = css`
  font-weight: bold;
  color: ${Colors.INK};
`;

const accountEmailStyle = css`
  ${p2Style}
  color: ${Colors.TEXT_SECONDARY};
  line-height: 130%;
  overflow-wrap: anywhere;
`;

const accountOptionsListStyle = css`
  margin-top: 1.75rem;
  margin-bottom: 2rem;
  list-style: none;
  display: flex;
  flex-direction: column;
  border: 1px solid ${Colors.BORDER};
  border-radius: ${Radius.lg};
  overflow: hidden;
  background: ${Colors.WHITE};

  > li + li {
    border-top: 1px solid ${Colors.BORDER};
  }
`;

const optionButtonStyle = css`
  ${unstyledButton}
  ${valueStyle}
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.9375rem 1rem;
  transition: background-color ${Motion.fast};

  > span {
    flex: 1;
    min-width: 0;
  }

  @media (hover: hover) {
    &:hover {
      background: ${Colors.SURFACE};
    }
  }

  &:active {
    background: ${Colors.SURFACE_MUTED};
  }

  &:focus-visible {
    outline: 2px solid ${Colors.CAPRI_BLUE};
    outline-offset: -2px;
  }
`;

const iconStyle = css`
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
  color: ${Colors.CAPRI_BLUE};
`;

const chevronRightStyle = css`
  width: 20px;
  height: 20px;
  flex: 0 0 auto;
  color: ${Colors.WINDOW_GRAY};
  margin-left: auto;
`;

const legalOptionsListStyle = css`
  padding-top: 0.5rem;
  list-style: none;
  display: flex;
  flex-direction: column;
`;

const legalButtonStyle = css`
  ${unstyledButton}
  ${valueStyle}
  display: flex;
  align-items: center;
  padding: 0.75rem 0;
  color: ${Colors.CAPRI_BLUE};
  transition: color ${Motion.fast};

  @media (hover: hover) {
    &:hover {
      color: ${Colors.CAPRI_BLUE_DARK};
      text-decoration: underline;
      text-underline-offset: 0.2em;
    }
  }
`;
